<script setup>
import { computed, ref, watch, reactive } from 'vue';
import { usePage, router } from '@inertiajs/vue3'
import { toast } from 'vue3-toastify';
import Input from '@/Components/Form/Input.vue'
import Select from '@/Components/Form/Select.vue'
import { useLoadingStore } from '@/Store/index'

const loadingStore = useLoadingStore();
const page = usePage()

const client = computed(() => page.props.client || null);
const states = computed(() => page.props.states || null);
const transactionSuccess = computed(() => page.props.transactionSuccess || false);
const serviceFeeGateway = computed(() => page.props.serviceFeeGateway || null);
const transactionId = computed(() => page.props.transactionId || null);

const stepper = ref(null);
const currentStep = ref(1);
const steps = ref([
    'What are you paying?',
    'Payment Details',
    'Review',
    'Receipt'
]);

const validateStep = (step) => {
    const requiredFields = {
        1: ['bill_amount', 'shipping_first_name', 'shipping_last_name', 'bill_type', 'shipping_address_1', 'shipping_city', 'shipping_state', 'shipping_zip'],
        2: ['card_number', 'expiry', 'cvv', 'email', 'phone', 'billing_address_1', 'billing_city', 'billing_state', 'billing_zip']
    };

    for (const field of requiredFields[step] || []) {
        if (!bill[field]) {
            toast.error('Please complete all required fields.');
            return false;
        }
    }
    return true;
};

const nextStep = () => {
    if (currentStep.value === 1 && !validateStep(1)) return;
    if (currentStep.value === 2) {
        if (sameAsShipping.value) prefillBilling();
        if (!validateStep(2)) return;
    }
    if (currentStep.value < steps.value.length) {
        document.querySelector('.v-stepper-window').scrollTop = 0;
        currentStep.value++;
    } else {
        toast.error('Please complete all required fields.');
    }
}

const prevStep = () => {
    if (currentStep.value > 1) currentStep.value--;
}

const bill = reactive({
    shipping_first_name: null,
    shipping_last_name: null,
    shipping_company: null,
    bill_type: null,
    bill_amount: null,
    payment_option: 'credit_card',
    account_no_comment: null,
    card_number: page.props.testing ? '4242424242424242' : null,
    expiry: page.props.testing ? '12/24' : null,
    cvv: page.props.testing ? '123' : null,
    shipping_first_name: null,
    shipping_last_name: null,
    shipping_company: null,
    shipping_city: client.value.prefill_city || null,
    shipping_state: client.value.prefill_state || null,
    shipping_zip: client.value.prefill_zip || null,
    shipping_address_1: null,
    shipping_address_2: null,
    billing_first_name: null,
    billing_last_name: null,
    billing_company: null,
    billing_city: client.value.prefill_city || null,
    billing_state: client.value.prefill_state || null,
    billing_zip: client.value.prefill_zip || null,
    billing_address_1: null,
    billing_address_2: null,
    email: null,
    phone: null,
});
const sameAsShipping = ref(true);
watch(sameAsShipping, (newVal) => {
    if (newVal) prefillBilling();
})
const prefillBilling = () => {
    bill.billing_first_name = bill.shipping_first_name;
    bill.billing_last_name = bill.shipping_last_name;
    bill.billing_company = bill.shipping_company;
    bill.billing_address_1 = bill.shipping_address_1;
    bill.billing_address_2 = bill.shipping_address_2;
    bill.billing_city = bill.shipping_city;
    bill.billing_state = bill.shipping_state;
    bill.billing_zip = bill.shipping_zip;
}

const serviceFee = computed(() => {
    if (serviceFeeGateway.value) {
        return (bill.bill_amount * (serviceFeeGateway.value.percent / 100)).toFixed(2);
    }
    return null;
})

const submit = () => {
    loadingStore.loading = true;
    router.post(window.location.href, bill, {
        onFinish: () => {
            loadingStore.loading = false;
            if (!transactionSuccess.value) {
                toast.error('Transaction failed. Please try again.');
            }
        },
    });
}

watch(transactionSuccess, (newVal) => {
    if (newVal) currentStep.value = steps.value.length;
})

window.document.title = `${client.value.name}`;

window.onkeydown = (e) => {
    if (e.key === 'Enter' && currentStep.value < 4) nextStep();
}

const currencyFormat = (float) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(float);
}

</script>

<style>
@media (min-width: 1024px) {
    .v-stepper-header {
        overflow: visible;
    }
    .v-stepper-window {
        overflow: scroll;
    }
    /* Ensure that the scrollbar is always showing on MacOS */
    .v-stepper-window::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: rgba(0, 0, 0, .5);
        box-shadow: 0 0 1px rgba(255, 255, 255, .5);
    }
    .v-stepper-window::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 4px;
    }
}

</style>

<template>
    <div class="grid h-screen md:grid-cols-2 grid-cols-1">
        <div class="z-20 md:h-full border-r-2 border-gray-50 w-full">
            <div class="relative top-0 z-10 flex h-full items-center justify-center bg-gradient-to-r from-orange-500 to-orange-100 opacity-30">
            </div>
            <div class="relative top-[-100%] z-10 flex h-full items-center justify-center py-8 md:py-0">
                <div class="flex flex-col items-center">
                    <img :src="client.logo_url" class="h-20" />
                    <p class="text-3xl font-bold text-white">{{ client.name }}</p>
                    <p class="text-1xl font-bold text-white" v-html="client.additional_information"></p>
                </div>
            </div>
            <div class="relative top-[-200%] z-0 h-full bg-cover backdrop-blur-sm" :style="`background-image: url('${client.bg_url}');`">
            </div>
        </div>
        <div class="col-span-2 flex md:h-screen h-full flex-col items-center gap-4 px-8 pt-8 md:col-span-1">
            <p class="text-3xl font-extralight ">Payment</p>
            <v-stepper :items="steps" class="w-full h-full !flex !flex-col p-0 pb-8 md:p-8" ref="stepper" hide-actions v-model="currentStep" >
                <template v-slot:item.1>
                    <div class="md:grid md:grid-cols-2 flex-col md:gap-4 gap-8 pb-12">
                        <p class="col-span-1 md:col-span-2 font-semibold text-lg">Details</p>
                        <Input label="First Name" v-model="bill.shipping_first_name" required class="col-span-1" />
                        <Input label="Last Name" v-model="bill.shipping_last_name" required class="col-span-1" />
                        
                        <Input class="col-span-1 md:col-span-2" label="Company" v-model="bill.shipping_company" />
                        <Input :label="'Account No / Comment' + (client.account_no_comment_limit ? ' (' + client.account_no_comment_limit + ' characters max)' : '')" v-model="bill.account_no_comment" class="col-span-1 md:col-span-2" :maxlength="client.account_no_comment_limit" />
                        <Input 
                        class="col-span-1 md:col-span-2"
                        label="Amount to Pay" 
                        type="currency" 
                        :options="{currency: 'USD', valueRange: { min: 0}}" 
                        v-model.number="bill.bill_amount" 
                        required 
                        />
                        <Select class="col-span-1 md:col-span-2" label="Bill Type" v-model="bill.bill_type" :options="client.client_bill_types.map(x => ({key: x.id, value: x.name}))" required />

                        <p class="col-span-1 md:col-span-2 font-semibold text-lg">Address</p>
                        <Input label="Address" v-model="bill.shipping_address_1" class="col-span-1 md:col-span-2" required />
                        <Input label="Address 2" v-model="bill.shipping_address_2" class="col-span-1 md:col-span-2" />
                        
                        <div class="col-span-2 flex-col md:grid grid-cols-6 gap-4">
                            <Input label="City" v-model="bill.shipping_city" class="col-span-2" required />
                            <Select label="State" v-model="bill.shipping_state" :options="states" class="col-span-2" required />
                            <Input label="Zip" v-model="bill.shipping_zip" mask="#####" class="col-span-2" required />
                        </div>

                        <p class="col-span-1 md:col-span-2 font-light text-lg">Payment Option</p>
                        <!-- <div class="col-span-1 md:col-span-2 flex items-center gap-4">
                            <label class="flex items-center gap-2">
                                <input type="radio" v-model="bill.payment_option" value="ach" class="form-radio h-4 w-4 text-blue-500 transition duration-150 ease-in-out border border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" disabled />
                                <span class="text-gray-400">ACH</span>
                            </label>
                        </div> -->
                        <div class="col-span-1 md:col-span-2 flex items-center gap-4">
                            <label class="flex items-center gap-2">
                                <input type="radio" v-model="bill.payment_option" value="credit_card" class="form-radio h-4 w-4 text-blue-500 transition duration-150 ease-in-out border border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" />
                                <span class="text-gray-700">Credit Card {{ serviceFeeGateway ? ' - ' + serviceFeeGateway.percent + '% Service Fee' : '' }}</span>
                            </label>
                        </div>
                    </div>
                </template>

                <template v-slot:item.2>
                    <div class="md:grid md:grid-cols-2 flex-col md:gap-2 pb-12">
                        <p class="col-span-4 py-2 font-semibold">Payment Details</p>
                        <Input label="Card Number" mask="####-####-####-####" class="col-span-4" type="text" v-model="bill.card_number" required />
                        <Input label="Expiry" mask="##/##" class="col-span-2" type="text" v-model="bill.expiry" required />
                        <Input label="CVV" mask="####" class="col-span-2" type="text" v-model="bill.cvv" required />

                        <div class="col-span-4 py-2 flex items-center">
                            <p class="font-semibold">Address</p>
                            <label class="ml-4 flex items-center">
                                <input type="checkbox" v-model="sameAsShipping" class="form-checkbox h-4 w-4 text-blue-500 transition duration-150 ease-in-out rounded border-gray-400" style="border: 1px solid gray;"/>
                                <span class="ml-2 text-gray-700">Same as Shipping</span>
                            </label>
                        </div>
                        <template v-if="!sameAsShipping">
                            <div class="grid grid-cols-6 col-span-4 gap-4">
                                <Input label="First Name" v-model="bill.billing_first_name" class="col-span-3" required />
                                <Input label="Last Name" v-model="bill.billing_last_name" class="col-span-3" required />
                                <Input label="Company" v-model="bill.billing_company" class="col-span-6" />

                                <Input label="Address" v-model="bill.billing_address_1" class="col-span-6" required />
                                <Input label="Address 2" v-model="bill.billing_address_2" class="col-span-6" />
                                <Input label="City" v-model="bill.billing_city" class="col-span-2" required />
                                <Select label="State" v-model="bill.billing_state" :options="states" class="col-span-2" required />
                                <Input label="Zip" v-model="bill.billing_zip" mask="#####" class="col-span-2" required />
                            </div>
                        </template>

                        <p class="col-span-4 py-2 font-semibold">Contact Details</p>
                        <Input label="Email" class="col-span-2" type="email" v-model="bill.email" required />
                        <Input label="Phone" mask="+1 (###) ### ####" class="col-span-2" type="tel" v-model="bill.phone" required />

                        <div>
                            <p class="col-span-4 py-2 font-semibold">Amount to be Paid</p>
                            <p class="col-span-4">{{ currencyFormat(bill.bill_amount) }}</p>
                        </div>
                        
                        <div>
                            <p class="col-span-1 py-2 font-semibold" v-if="serviceFee">Service Charge</p>
                            <p class="col-span-1" v-if="serviceFee">{{ currencyFormat(serviceFee) }}</p>
                        </div>
                    </div>
                </template>

                <template v-slot:item.3>
                    <div class="md:grid md:grid-cols-2 flex-col md:gap-2 pb-12">
                        <p class="col-span-2 text-2xl font-semibold">Review Your Transaction</p>

                        <p class="font-semibold">Amount to Pay:</p>
                        <p>{{ currencyFormat(bill.bill_amount) }}</p>

                        <p class="font-semibold" v-if="serviceFee">Service Fee:</p>
                        <p v-if="serviceFee">{{ currencyFormat(serviceFee) }}</p>

                        <p class="col-span-2 text-xl font-semibold">Shipping Information</p>

                        <p class="font-semibold">Name:</p>
                        <p>{{ bill.shipping_first_name }} {{ bill.shipping_last_name }}</p>

                        <p class="font-semibold">Address:</p>
                        <div>
                            <p>{{ bill.shipping_address_1 }}</p>
                            <p>{{ bill.shipping_address_2 }}</p>
                        </div>

                        <p class="font-semibold">Company:</p>
                        <p>{{ bill.shipping_company }}</p>

                        <p class="font-semibold">City:</p>
                        <p>{{ bill.shipping_city }}</p>

                        <p class="font-semibold">State:</p>
                        <p>{{ states.find(state => state.key === bill.shipping_state)?.value }}</p>

                        <p class="font-semibold">Zip:</p>
                        <p>{{ bill.shipping_zip }}</p>

                        <p class="col-span-2 text-xl font-semibold">Billing Information</p>

                        <p class="font-semibold">Name:</p>
                        <p>{{ bill.billing_first_name }} {{ bill.billing_last_name }}</p>



                        <p class="font-semibold">Address:</p>
                        <div>
                            <p>{{ bill.billing_address_1 }}</p>
                            <p>{{ bill.billing_address_2 }}</p>
                        </div>

                        <p class="font-semibold">Company:</p>
                        <p>{{ bill.billing_company }}</p>

                        <p class="font-semibold">City:</p>
                        <p>{{ bill.billing_city }}</p>

                        <p class="font-semibold">State:</p>
                        <p>{{ states.find(state => state.key === bill.billing_state)?.value }}</p>

                        <p class="font-semibold">Zip:</p>
                        <p>{{ bill.billing_zip }}</p>

                        <p class="font-semibold">Email:</p>
                        <p>{{ bill.email }}</p>

                        <p class="font-semibold">Phone:</p>
                        <p>{{ bill.phone }}</p>


                        <p class="col-span-2 text-xl font-semibold">Payment Information</p>

                        <p class="font-semibold">Payment Option:</p>
                        <p>{{ bill.payment_option === 'ach' ? 'ACH' : 'Credit Card' }}</p>
                    </div>
                </template>

                <template v-slot:item.4>
                    <div class="md:grid md:grid-cols-2 flex-col md:gap-2 pb-12">
                        <p class="col-span-2 text-2xl font-semibold">Receipt</p>

                        <p class="font-semibold">Transaction ID:</p>
                        <p>{{ transactionId }}</p>

                        <p class="font-semibold">Amount paid:</p>
                        <p>{{ currencyFormat(bill.bill_amount) }}</p>

                        <p class="font-semibold" v-if="serviceFee">Service Fee:</p>
                        <p v-if="serviceFee">{{ currencyFormat(serviceFee) }}</p>

                        <p class="col-span-2 text-xl font-semibold">Shipping Information</p>

                        <p class="font-semibold">Name:</p>
                        <p>{{ bill.shipping_first_name }} {{ bill.shipping_last_name }}</p>

                        <p class="font-semibold">Address:</p>
                        <div>
                            <p>{{ bill.shipping_address_1 }}</p>
                            <p>{{ bill.shipping_address_2 }}</p>
                        </div>

                        <p class="font-semibold">Company:</p>
                        <p>{{ bill.shipping_company }}</p>

                        <p class="font-semibold">City:</p>
                        <p>{{ bill.shipping_city }}</p>

                        <p class="font-semibold">State:</p>
                        <p>{{ states.find(state => state.key === bill.shipping_state)?.value }}</p>

                        <p class="font-semibold">Zip:</p>
                        <p>{{ bill.shipping_zip }}</p>

                        <p class="col-span-2 text-xl font-semibold">Billing Information</p>

                        <p class="font-semibold">Name:</p>
                        <p>{{ bill.billing_first_name }} {{ bill.billing_last_name }}</p>



                        <p class="font-semibold">Address:</p>
                        <div>
                            <p>{{ bill.billing_address_1 }}</p>
                            <p>{{ bill.billing_address_2 }}</p>
                        </div>

                        <p class="font-semibold">Company:</p>
                        <p>{{ bill.billing_company }}</p>

                        <p class="font-semibold">City:</p>
                        <p>{{ bill.billing_city }}</p>

                        <p class="font-semibold">State:</p>
                        <p>{{ states.find(state => state.key === bill.billing_state)?.value }}</p>

                        <p class="font-semibold">Zip:</p>
                        <p>{{ bill.billing_zip }}</p>

                        <p class="font-semibold">Email:</p>
                        <p>{{ bill.email }}</p>

                        <p class="font-semibold">Phone:</p>
                        <p>{{ bill.phone }}</p>


                        <p class="col-span-2 text-xl font-semibold">Payment Information</p>

                        <p class="font-semibold">Payment Option:</p>
                        <p>{{ bill.payment_option === 'ach' ? 'ACH' : 'Credit Card' }}</p>
                    </div>
                </template>
                <div class="grid grid-cols-2 gap-4 px-6 mt-auto" v-if="currentStep !== 5 && transactionSuccess == false">
                    <a @click="prevStep">
                        <button :disabled="currentStep === 1" :style="`background-color: ${client.color}`" class="text-white px-4 py-2 rounded-md w-full disabled:opacity-50">Previous</button>
                    </a>
                    <a @click="nextStep" v-if="currentStep !== steps.length - 1">
                        <button :disabled="currentStep === steps.length - 1" :style="`background-color: ${client.color}`" class="text-white px-4 py-2 rounded-md w-full disabled:opacity-50">Next</button>
                    </a>
                    <a @click="submit" v-else-if="currentStep === steps.length - 1">
                        <button :style="`background-color: ${client.color}`" class="text-white px-4 py-2 rounded-md w-full disabled:opacity-50">Submit</button>
                    </a>
                </div>
            </v-stepper>
        </div>
    </div>
</template>